import { User } from '../../types';

export interface LoginRequest {
  telefono: string;
  password: string;
}

export interface RegisterRequest {
  telefono: string;
  password: string;
  nombreUsuario: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken?: string;
}

export interface ApiError {
  message: string;
  code?: string;
  details?: any;
}

class AuthService {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // Configura la URL base de tu API C#
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7123/api';
    this.token = localStorage.getItem('authToken');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json().catch(() => ({
        message: 'Error en la conexión con el servidor',
      }));
      
      throw new Error(errorData.message || `Error HTTP: ${response.status}`);
    }

    return response.json();
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });

      // Guardar token (el usuario se extrae del JWT)
      this.token = response.token;
      localStorage.setItem('authToken', response.token);

      return response;
    } catch (error) {
      console.error('Login service error:', error);
      throw error;
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await this.request<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      // Guardar token (el usuario se extrae del JWT)
      this.token = response.token;
      localStorage.setItem('authToken', response.token);

      return response;
    } catch (error) {
      console.error('Register service error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      // Llamar al endpoint de logout si existe
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout service error:', error);
    } finally {
      // Limpiar tokens locales independientemente del resultado
      this.token = null;
      localStorage.removeItem('authToken');
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await this.request<AuthResponse>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      });

      // Actualizar tokens
      this.token = response.token;
      localStorage.setItem('authToken', response.token);
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }

      return response;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Si falla el refresh, limpiar todo
      this.token = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    try {
      const user = await this.request<User>('/auth/me');
      return user;
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Decodifica el payload del JWT
  private decodeToken(token: string): Record<string, any> | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload;
    } catch {
      return null;
    }
  }

  // Extrae los datos del usuario directamente de los claims del JWT
  getUserFromToken(): User | null {
    if (!this.token) return null;

    const payload = this.decodeToken(this.token);
    if (!payload) return null;

    // .NET uses long claim names like "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
    const roleClaim = payload.role 
      || payload.nombreRol 
      || payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
      || undefined;
    
    // DEBUG: Log JWT payload to see what claims are present
    console.log('JWT Payload:', payload);
    console.log('Role claim value:', roleClaim);

    return {
      id: payload.sub || '',
      name: payload.unique_name || '',
      phone: payload.phone || '',
      imagenUrl: payload.imagenUrl || undefined,
      empresaId: payload.empresaId ? Number(payload.empresaId) : undefined,
      clienteId: payload.clienteId ? Number(payload.clienteId) : undefined,
      nombreRol: roleClaim,
      createdAt: payload.createdAt ? new Date(payload.createdAt) : new Date(),
    };
  }

  // Método para verificar si el token está expirado (si usas JWT)
  isTokenExpired(): boolean {
    if (!this.token) return true;
    
    const payload = this.decodeToken(this.token);
    if (!payload) return true;

    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  }
}

export const authService = new AuthService();
